import type { ReactNode } from 'react';
import { Component, useEffect, useState } from 'react';
import { navigate } from '@/features/routing';
import type { IErrorBoundaryProps, IErrorBoundaryState } from './types';

/**
 * Error boundary component that handles errors in the React component tree.
 * In development mode, it re-throws errors for Next.js to display.
 * In production mode, it redirects users to the home page.
 */
export class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): IErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // In development mode, re-throw the error for Next.js to handle.
    if (process.env.NODE_ENV === 'development') {
      throw error;
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // In development, let Next.js handle the error display.
      if (process.env.NODE_ENV === 'development') {
        throw this.state.error;
      }

      // In production, show fallback UI or redirect.
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} resetError={this.handleReset} />;
      }

      // Default fallback: show error message and redirect after mount.
      return <ErrorFallback error={this.state.error!} />;
    }

    return this.props.children;
  }
}

/**
 * Fallback component that handles the redirect after mounting.
 */
const ErrorFallback = (
  {
    // error,
  }: {
    error: Error;
  }
) => {
  const [secondsLeft, setSecondsLeft] = useState(3);
  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((seconds) => seconds - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (secondsLeft === 0) {
      navigate('/');
    }
  }, [secondsLeft, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-red-600">Something went wrong</h1>
        <p className="mt-2 text-gray-600">Redirecting to home page in {secondsLeft}...</p>
      </div>
    </div>
  );
};

/**
 * Hook-based error boundary wrapper for use in client components.
 */
export const ErrorBoundaryWrapper = ({ children, fallback }: IErrorBoundaryProps) => {
  return <ErrorBoundary fallback={fallback}>{children}</ErrorBoundary>;
};
